// Функции расчёта нагрева кабеля по длительно допустимому току

import type {
  Material,
  InsulationType,
  InstallationMethod,
  CalculationInput,
  HeatingResult
} from '../types';
import { STANDARD_SECTIONS } from '../data/constants';
import {
  getTempCoefficient,
  getBundleCoefficient,
  getGroundCoefficient,
  getAllowedCurrent
} from '../data/tables';

/**
 * Преобразовать тип изоляции для таблиц
 * paper обрабатывается как pvc (близкие характеристики)
 */
function normalizeInsulationType(insulationType: InsulationType): 'pvc' | 'xlpe' {
  return insulationType === 'xlpe' ? 'xlpe' : 'pvc';
}

/**
 * Получить температурный коэффициент (обёртка для экспорта)
 */
export function getTemperatureCoefficient(
  insulationType: InsulationType,
  ambientTemp: number
): number {
  return getTempCoefficient(ambientTemp, normalizeInsulationType(insulationType));
}

/**
 * Получить коэффициент прокладки пучком (обёртка для экспорта)
 */
export function getBundleCoeff(cablesCount: number): number {
  return getBundleCoefficient(cablesCount);
}

/**
 * Получить коэффициент прокладки в земле (обёртка для экспорта)
 */
export function getGroundCoeff(thermalResistance: number): number {
  return getGroundCoefficient(thermalResistance);
}

/**
 * Получить допустимый ток для сечения (обёртка для экспорта)
 */
export function getAllowedCurrentForSection(
  material: Material,
  insulationType: InsulationType,
  installationMethod: InstallationMethod,
  crossSection: number
): number {
  // Для трубы и лотка используем коэффициенты как для воздуха
  const effectiveMethod = (installationMethod === 'pipe' || installationMethod === 'tray' || installationMethod === 'bundle')
    ? 'air'
    : installationMethod;
  
  return getAllowedCurrent(crossSection, material, normalizeInsulationType(insulationType), effectiveMethod as 'air' | 'ground');
}

/**
 * Расчёт общего поправочного коэффициента
 * kпопр = kтемп × kпучок × kземля
 */
export function calculateCorrectionFactor(
  insulationType: InsulationType,
  ambientTemp: number,
  cablesCount: number,
  installationMethod: InstallationMethod,
  thermalResistance?: number
): number {
  // Температурный коэффициент
  const kTemp = getTempCoefficient(ambientTemp, normalizeInsulationType(insulationType));
  
  // Коэффициент прокладки пучком
  let kBundle = 1;
  if (installationMethod === 'bundle' || cablesCount > 1) {
    kBundle = getBundleCoefficient(cablesCount);
  }
  
  // Коэффициент для прокладки в трубе
  let kPipe = 1;
  if (installationMethod === 'pipe') {
    kPipe = 0.9; // Усреднённый коэффициент для трубы
  }
  
  // Коэффициент для прокладки в лотке
  let kTray = 1;
  if (installationMethod === 'tray') {
    kTray = 0.95; // Усреднённый коэффициент для лотка
  }
  
  // Коэффициент для прокладки в земле
  let kGround = 1;
  if (installationMethod === 'ground' && thermalResistance !== undefined) {
    kGround = getGroundCoefficient(thermalResistance);
  }
  
  return kTemp * kBundle * kPipe * kTray * kGround;
}

/**
 * Основная функция расчёта нагрева
 * Проверяет условие: Iрасч ≤ Iдоп × kпопр
 */
export function calculateHeating(input: CalculationInput): HeatingResult {
  const {
    current,
    crossSection,
    material,
    insulationType,
    installationMethod,
    ambientTemp,
    cablesCount,
  } = input;

  // Тепловое сопротивление грунта (по умолчанию 120 К·см/Вт - нормальный грунт)
  const thermalResistance = input.thermalResistance ?? 120;

  // Получаем табличный допустимый ток
  const allowedCurrent = getAllowedCurrentForSection(
    material,
    insulationType,
    installationMethod,
    crossSection
  );

  // Рассчитываем поправочный коэффициент
  const correctionFactor = calculateCorrectionFactor(
    insulationType,
    ambientTemp,
    cablesCount,
    installationMethod,
    thermalResistance
  );

  // Скорректированный допустимый ток
  const correctedAllowedCurrent = allowedCurrent * correctionFactor;

  // Проверка условия
  const isWithinNorm = current <= correctedAllowedCurrent;

  // Запас по току: запас% = ((Iдоп × kпопр) / Iрасч - 1) × 100%
  const currentMargin = current > 0 
    ? ((correctedAllowedCurrent / current) - 1) * 100 
    : 0;

  // Подбираем рекомендуемое сечение
  const recommendedSection = selectSectionByHeating(
    current,
    material,
    insulationType,
    installationMethod,
    correctionFactor
  );

  return {
    calculatedCurrent: current,
    allowedCurrent,
    correctionFactor,
    correctedAllowedCurrent,
    currentMargin,
    recommendedSection: recommendedSection ?? crossSection,
    isWithinNorm,
  };
}

/**
 * Подбор минимального сечения по нагреву
 * Алгоритм:
 * 1. Вычислить требуемый табличный ток: Iтреб = Iрасч / kпопр
 * 2. Выбрать наименьшее сечение из стандартного ряда, для которого Iдоп(S) ≥ Iтреб
 */
export function selectSectionByHeating(
  current: number,
  material: Material,
  insulationType: InsulationType,
  installationMethod: InstallationMethod,
  correctionFactor: number
): number | null {
  if (current <= 0) {
    return null;
  }

  // Требуемый табличный ток
  const requiredCurrent = current / correctionFactor;

  // Определяем эффективный способ прокладки
  const effectiveMethod = (installationMethod === 'pipe' || installationMethod === 'tray' || installationMethod === 'bundle')
    ? 'air'
    : installationMethod;

  // Ищем минимальное сечение
  for (const section of STANDARD_SECTIONS) {
    const allowedCurrent = getAllowedCurrent(
      section,
      material,
      normalizeInsulationType(insulationType),
      effectiveMethod as 'air' | 'ground'
    );
    
    if (allowedCurrent >= requiredCurrent) {
      return section;
    }
  }

  // Если не нашли подходящее сечение, возвращаем максимальное
  return STANDARD_SECTIONS[STANDARD_SECTIONS.length - 1];
}

/**
 * Подбор сечения с полным расчётом (для использования в UI)
 */
export function selectSectionWithCalculation(
  input: CalculationInput
): { section: number | null; result: HeatingResult } {
  const {
    current,
    material,
    insulationType,
    installationMethod,
    ambientTemp,
    cablesCount,
  } = input;

  const thermalResistance = input.thermalResistance ?? 120;

  // Рассчитываем поправочный коэффициент
  const correctionFactor = calculateCorrectionFactor(
    insulationType,
    ambientTemp,
    cablesCount,
    installationMethod,
    thermalResistance
  );

  // Подбираем сечение
  const section = selectSectionByHeating(
    current,
    material,
    insulationType,
    installationMethod,
    correctionFactor
  );

  // Если сечение найдено, рассчитываем результат для него
  if (section !== null) {
    const newInput = { ...input, crossSection: section };
    const result = calculateHeating(newInput);
    return { section, result };
  }

  // Возвращаем результат для текущего сечения
  const result = calculateHeating(input);
  return { section: null, result };
}

// Экспорт функций из tables.ts для удобства
export {
  getTempCoefficient,
  getBundleCoefficient,
  getGroundCoefficient,
  getAllowedCurrent
} from '../data/tables';
